import type { ReactNode } from 'react';

type Options = {
  seed: string;
  totalCount?: number;
  minStarSize?: number;
  maxStarSize?: number;
  distributionFactor?: number; // Controls how quickly probability decreases for larger stars
  numberOfClusters?: number;
};

export type Star = {
  x: number;
  y: number;
  size: number;
  rotation: number;
  filled: boolean;
};

const simpleHash = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

const seededRandom = (seed: string): (() => number) => {
  let x = simpleHash(seed || Date.now().toString());
  return () => {
    x = (x * 1103515245 + 12345) & 0x7fffffff;
    return x / 0x7fffffff;
  };
};

const getWeightedSize = (
  random: () => number,
  minSize: number,
  maxSize: number,
  distributionFactor: number
): number => {
  const x = random();
  const weighted = Math.pow(x, distributionFactor);
  return minSize + weighted * (maxSize - minSize);
};

const generateClusterStars = (
  centerX: number,
  centerY: number,
  random: () => number,
  minStarSize: number,
  maxClusterRadius: number
): Star[] => {
  const numberOfStarsInCluster = Math.floor(random() * 8) + 10; // 8-15 stars
  const clusterStars: Star[] = [];

  for (let i = 0; i < numberOfStarsInCluster; i++) {
    const angle = random() * Math.PI * 2;
    const radius = random() * maxClusterRadius;
    const x = centerX + Math.cos(angle) * radius;
    const y = centerY + Math.sin(angle) * radius;

    clusterStars.push({
      x,
      y,
      size: minStarSize,
      rotation: random() * 360,
      filled: Math.round(random()) % 2 === 0,
    });
  }

  return clusterStars;
};

export const getStars = ({
  seed,
  totalCount = 20,
  minStarSize = 6,
  maxStarSize = 30,
  distributionFactor = 4,
  numberOfClusters = 0,
}: Options): Star[] => {
  const random = seededRandom(seed);
  const placedStars: Star[] = [];

  const isOverlapping = (newStar: { x: number; y: number; size: number }) => {
    return placedStars.some((star) => {
      const dx = newStar.x - star.x;
      const dy = newStar.y - star.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      return distance < (newStar.size + star.size) / 2;
    });
  };

  // First, place all the regular stars
  while (placedStars.length < totalCount) {
    const size = getWeightedSize(
      random,
      minStarSize,
      maxStarSize,
      distributionFactor
    );
    const x = random() * (window.innerWidth || 1000) - size / 2;
    const y = random() * (window.innerHeight || 1000) - size / 2;
    const rotation = random() * 360;
    const newStar = {
      x,
      y,
      size,
      rotation,
      filled: size >= maxStarSize * 0.8 ? true : Math.round(random()) % 2 === 0,
    };

    if (!isOverlapping(newStar)) {
      placedStars.push(newStar);
    }
  }

  if (numberOfClusters > 0) {
    const maxClusterRadius =
      Math.min(window.innerWidth, window.innerHeight) * 0.1;

    for (let i = 0; i < numberOfClusters; i++) {
      const clusterCenterX = random() * (window.innerWidth || 1000);
      const clusterCenterY = random() * (window.innerHeight || 1000);

      const clusterStars = generateClusterStars(
        clusterCenterX,
        clusterCenterY,
        random,
        minStarSize,
        maxClusterRadius
      );

      // Add all cluster stars that don't overlap with existing stars
      clusterStars.forEach((star) => {
        if (!isOverlapping(star)) {
          placedStars.push(star);
        }
      });
    }
  }

  return placedStars;
};

type IllustrationPlacementOptions = {
  seed: string;
  illustrations: ReactNode[];
  minSize?: number;
  maxSize?: number;
  totalCount?: number;
};

export type PlacedIllustration = {
  illustration: ReactNode;
  x: number;
  y: number;
  size: number;
  rotation: number;
};

/**
 * Randomly places illustrations on the screen without overlap
 * @param options - Configuration for illustration placement
 * @returns Array of positioned illustrations
 */
export const getRandomIllustrations = ({
  seed,
  illustrations,
  minSize = 40,
  maxSize = 120,
  totalCount = illustrations.length,
}: IllustrationPlacementOptions): PlacedIllustration[] => {
  const random = seededRandom(seed);
  const placed: PlacedIllustration[] = [];

  // SSR-safe window dimensions
  const width = typeof window !== 'undefined' ? window.innerWidth : 1000;
  const height = typeof window !== 'undefined' ? window.innerHeight : 1000;

  const isOverlapping = (newItem: { x: number; y: number; size: number }) => {
    return placed.some((item) => {
      const dx = newItem.x - item.x;
      const dy = newItem.y - item.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      return distance < (newItem.size + item.size) / 2;
    });
  };

  let tries = 0;
  const maxAttempts = Math.max(totalCount * 20, 100); // More attempts for dense placements

  while (placed.length < totalCount && tries < maxAttempts) {
    const illustration = illustrations[placed.length % illustrations.length];
    const size = minSize + random() * (maxSize - minSize);
    const x = random() * width - size / 2;
    const y = random() * height - size / 2;
    const rotation = random() * 360;
    const newItem = { illustration, x, y, size, rotation };
    if (!isOverlapping(newItem)) {
      placed.push(newItem);
    }
    tries++;
  }

  return placed;
};
